import { useTranslation } from "@sg/i18n";
import { handleAddToCollectionOp } from "@sg/query";
import { notify } from "@sg/ui";
import { downloadFile } from "@sg/utils";
import { useMutation, useQuery } from "react-query";
import { z } from "zod";
import { useDefaultMarginGroup } from "~/pages/Counterparty/hooks";
import useSettingPrefStore from "~/store/setttingPref";
import { EquitySwapType, LoanType, SpotType, Status, TransactionType } from "~/types";
import { agent } from "~/util/agent";
import { useCounterpartyContextQueryParams } from "../util";
import { DocumentResourceType, useDocMetadata, useDocsMetadata, useDownloadDoc, useUploadDoc } from "./document";

export const QUERY_TX_KEY = "tx";

const QUERY_POSITIONS_KEY = "tx";

const PositionAmountSchema = z
  .object({
    amount: z.string(),
  })
  .optional();

const PositionSchema = z.record(
  z.string().uuid(),
  z.record(
    z.string(),
    z.object({
      Short: PositionAmountSchema,
      Long: PositionAmountSchema,
    })
  )
);

const PendingTxSchema = z.object({
  id: z.string(),
  asset: z.string(),
  quantity: z.string(),
  type: z.string(),
  status: z.nativeEnum(Status),
  active_transfer_type: z.string(),
  sender: z.string(),
  receiver: z.string(),
});

const KorDetailSchema = z.object({
  clientSubmittedFields: z.array(z.object({ category: z.string(), name: z.string(), value: z.string() })),
  korCalculatedFields: z.array(z.object({ category: z.string(), name: z.string(), value: z.string() })),
});

type PositionResourceType = z.infer<typeof PositionSchema>;
type PositionAmountResourceType = z.infer<typeof PositionAmountSchema>;
type PendingTxSchemaType = z.infer<typeof PendingTxSchema>;
type KorDetailType = z.infer<typeof KorDetailSchema>;

type BaseTransactionResourceType = (LoanType | SpotType | EquitySwapType) & { id: string; status: Status };

const usePositionsByCPContext = () => {
  const queryParams = useCounterpartyContextQueryParams().toString();

  return useQuery([QUERY_POSITIONS_KEY, queryParams], async () =>
    agent(`transactions/positions?${queryParams}`).json<PositionResourceType>()
  );
};

const useTxsByCPContext = () => {
  const queryParams = useCounterpartyContextQueryParams().toString();

  return useQuery([QUERY_TX_KEY, "ALL", queryParams], async () =>
    agent(`transactions/?${queryParams}`).json<BaseTransactionResourceType[]>()
  );
};

const usePendingTxs = () => {
  const entity = useSettingPrefStore((state) => state.entity);

  return useQuery([QUERY_TX_KEY, "PENDING", entity], async () =>
    agent(`transactions/pending?counterparty_ids=${entity}`).json<PendingTxSchemaType[]>()
  );
};

const getSDRPublicDataUti = async (transactionID: string) => {
  const params = new URLSearchParams();
  params.append("transaction_id", transactionID);
  params.append("submission_type", "PUBLIC");
  const response = await agent(`reporting/internal_reports?${params}`).json<
    { submission_id: string; submission_type: string }[]
  >();
  const submissionId = response.find((r) => r.submission_type.toUpperCase() === "PUBLIC")?.submission_id;
  if (submissionId) {
    const detail = await agent(`reporting/kor/submissions/${submissionId}/detail`).json<KorDetailType>();
    const uti = detail.clientSubmittedFields.find((f) => f.name.toLowerCase() === "uniquetransactionidentifier")?.value;
    return uti;
  }
  return "";
};

const useSDRPublicForTransaction = (transactionID = "") => {
  const params = new URLSearchParams();
  params.append("transaction_id", transactionID);
  params.append("submission_type", "PUBLIC");

  return useQuery(
    [QUERY_TX_KEY, "SDR", transactionID],
    async () => {
      const response = await agent(`reporting/internal_reports?${params}`).json<
        { submission_id: string; submission_type: string }[]
      >();
      const submissionId = response.find((r) => r.submission_type.toUpperCase() === "PUBLIC")?.submission_id;
      if (submissionId) {
        const detail = await agent(`reporting/kor/submissions/${submissionId}/detail`).json<KorDetailType>();
        const disseminationId = detail.korCalculatedFields.find(
          (f) => f.name.toLowerCase() === "disseminationidentifier"
        )?.value;
        return {
          disseminationId,
          submissionId,
        };
      }
      return Promise.resolve(null);
    },
    {
      enabled: !!transactionID,
    }
  );
};

const useTxs = (type: TransactionType) => {
  type DataType = EquitySwapType | LoanType | SpotType;
  const group = useDefaultMarginGroup();
  return useQuery(
    [QUERY_TX_KEY, type],
    async () => agent(`transactions/${type}/?margin_group_id=${group?.id}`).json<DataType[]>(),
    {
      enabled: !!group,
    }
  );
};

const useEquitySwapDetails = (id?: string) => {
  return useQuery(
    [QUERY_TX_KEY, TransactionType.EQUITY_SWAP, id],
    async () => agent(`transactions/${TransactionType.EQUITY_SWAP}/${id}`).json<EquitySwapType>(),
    {
      enabled: !!id,
    }
  );
};

const useCreateTx = (type: TransactionType) => {
  type DataType = EquitySwapType | LoanType | SpotType;
  return useMutation<unknown, Error, DataType>(
    (data) => {
      return agent.post(`transactions/${type}/`, { labels: ["created", "transaction"], json: data });
    },
    { ...handleAddToCollectionOp([QUERY_TX_KEY, type]) }
  );
};

const useTxDocsMetadata = (txId?: string) => {
  return useDocsMetadata([QUERY_TX_KEY, "docs-metadata", txId], `transactions/${txId}`, !!txId);
};

const useTxDocMetadata = (txId?: string, documentId?: string) => {
  return useDocMetadata(
    [QUERY_TX_KEY, "doc-metadata", txId, documentId],
    `transactions/${txId}`,
    documentId,
    !!documentId && !!txId
  );
};

const useUploadTxDoc = (txId?: string) => {
  return useUploadDoc(`transactions/${txId}`, [QUERY_TX_KEY, "docs-metadata", txId]);
};

const useDownloadTxDoc = (txId?: string) => {
  return useDownloadDoc(`transactions/${txId}`);
};

const useDownloadAllTxDocs = () => {
  const { t } = useTranslation();
  return useMutation<void, Error, string>(async (txId: string) => {
    const docs = await agent(`transactions/${txId}/docs/metadata`)
      .json<DocumentResourceType[]>()
      .catch(() => {
        notify.alert(t("result_failure", { resource: t("resource:documents"), verb: t("downloaded") }));
      });
    if (docs) {
      await Promise.all(
        docs.map(async (d) => {
          const file = await agent(`transactions/${txId}/docs/${d.id}`).blob();
          downloadFile(file, d.file_name);
        })
      ).catch(() => {
        notify.alert(t("result_failure", { resource: t("resource:documents"), verb: t("downloaded") }));
      });
    }
  });
};

export {
  getSDRPublicDataUti,
  PositionAmountSchema,
  PositionSchema,
  useCreateTx,
  useDownloadAllTxDocs,
  useDownloadTxDoc,
  useEquitySwapDetails,
  usePendingTxs,
  usePositionsByCPContext,
  useSDRPublicForTransaction,
  useTxDocMetadata,
  useTxDocsMetadata,
  useTxs,
  useTxsByCPContext,
  useUploadTxDoc,
};
export type { BaseTransactionResourceType, PendingTxSchemaType, PositionAmountResourceType, PositionResourceType };
